import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Grid,
  Alert,
  Button,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
  Divider,
  Chip
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import UndoIcon from '@mui/icons-material/Undo';
import { BALANCO_CAMPOS } from '../../config/planoContas';
// A lista sai do catalogo, nao de uma constante escrita a mao: lista manual
// desatualiza no primeiro indicador novo, e o usuario decide pular o balanco
// olhando justamente para ela.
import { indicadoresBloqueadosSemBalanco } from '../../diagnostico/catalogo';

const moeda = (valor) =>
  (Number(valor) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const Secao = ({ titulo, campos, valores, onChange }) => (
  <Card variant="outlined" sx={{ mb: 2 }}>
    <CardContent>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        {titulo}
      </Typography>
      <Grid container spacing={2}>
        {campos.map((c) => (
          <Grid item xs={12} sm={6} key={c.campo}>
            <Tooltip title={c.ajuda} placement="top-start">
              <TextField
                fullWidth
                size="small"
                label={c.nome}
                placeholder="0,00"
                value={valores[c.campo] ?? ''}
                onChange={(e) =>
                  onChange({
                    ...valores,
                    [c.campo]: e.target.value.replace(/[^\d.,-]/g, '').replace(',', '.')
                  })
                }
                inputProps={{ inputMode: 'decimal' }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>
                }}
              />
            </Tooltip>
          </Grid>
        ))}
      </Grid>
    </CardContent>
  </Card>
);

/**
 * Etapa opcional. Sem balanço os indicadores de liquidez, endividamento e
 * retorno não têm como ser calculados — o dashboard marca esses como
 * "requer balanço" em vez de inventar número.
 */
const StepBalanco = ({ balanco, onChange }) => {
  const preenchido = balanco.preenchido !== false;

  const setSecao = (secao, valores) => onChange({ ...balanco, [secao]: valores, preenchido: true });

  const ativoTotal =
    BALANCO_CAMPOS.ativo.reduce((s, c) => s + (Number(balanco.ativo?.[c.campo]) || 0), 0);
  const passivoTotal =
    BALANCO_CAMPOS.passivo.reduce((s, c) => s + (Number(balanco.passivo?.[c.campo]) || 0), 0);

  if (!preenchido) {
    return (
      <Box>
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.700', color: 'common.white', borderRadius: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            Balanço patrimonial — pulado
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.95 }}>
            Sem problema. O dashboard vai sair com os indicadores de resultado.
          </Typography>
        </Box>

        <Alert severity="warning" sx={{ mb: 3 }}>
          Estes indicadores ficarão marcados como <strong>requer balanço</strong>:
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
            {indicadoresBloqueadosSemBalanco().map((i) => (
              <Chip key={i} label={i} size="small" />
            ))}
          </Box>
        </Alert>

        <Button
          variant="outlined"
          startIcon={<UndoIcon />}
          onClick={() => onChange({ ...balanco, preenchido: true })}
        >
          Quero preencher o balanço
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 1 }}>
        <Typography variant="h6" fontWeight="bold">
          Balanço patrimonial (opcional)
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.95 }}>
          Foto de hoje: o que a empresa tem e o que deve.
        </Typography>
      </Box>

      <Alert severity="info" icon={<HelpOutlineIcon />} sx={{ mb: 3 }}>
        Preencher libera liquidez, endividamento, ROE e ROA. Não sabe agora?
        Pule — dá para completar depois sem refazer o DRE.
      </Alert>

      <Secao
        titulo="Ativo — o que a empresa tem"
        campos={BALANCO_CAMPOS.ativo}
        valores={balanco.ativo || {}}
        onChange={(v) => setSecao('ativo', v)}
      />

      <Secao
        titulo="Passivo — o que a empresa deve"
        campos={BALANCO_CAMPOS.passivo}
        valores={balanco.passivo || {}}
        onChange={(v) => setSecao('passivo', v)}
      />

      <Secao
        titulo="Patrimônio líquido"
        campos={BALANCO_CAMPOS.patrimonio}
        valores={balanco.patrimonio || {}}
        onChange={(v) => setSecao('patrimonio', v)}
      />

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">Total do ativo</Typography>
          <Typography variant="h6" fontWeight="bold">{moeda(ativoTotal)}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">Total do passivo</Typography>
          <Typography variant="h6" fontWeight="bold">{moeda(passivoTotal)}</Typography>
        </Grid>
      </Grid>

      <Button
        variant="text"
        color="inherit"
        startIcon={<SkipNextIcon />}
        onClick={() => onChange({ ...balanco, preenchido: false })}
      >
        Pular esta etapa
      </Button>
    </Box>
  );
};

export default StepBalanco;
